import axios from "axios";
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import Accordion from "../../../components/Accordion";
import { Row, Col } from "react-bootstrap";
import { AsOfAgingForm } from "./AsOfAgingForm";
import ErrorHandler from "../../../components/core/ErrorHandler";
import { DetailsModal } from "../../../components/grid/DetailsModal";

import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import SharedMainSelectableServerGrid from "shared/components/grids/serverGrid/SharedMainSelectableServerGrid";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";

import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ZIndexContext } from "@progress/kendo-react-common";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
} from "../../../components/grid/Kendo/CustomKendoGridCells";

import { PromiseToPayHeaderCell } from "./components/PromiseToPayHeaderCell";
import { UserContext } from "shared/context/UserContext";
import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../../components/grid/Kendo/KendoGridAndColumnConstants";

import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import { ConstructAgingPeriodAggregateSettingsHelper } from "./helpers/ConstructAgingPeriodAggregateSettingsHelper";
import { SetSummaryDetailsDataHelper } from "./helpers/SetSummaryDetailsDataHelper";

export function AsOfAging(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const agingPeriods = passedProps.agingPeriods;

  const userConfig = useContext(UserContext);

  const DETAILS_DATA_ITEM_KEY = "claimId";
  const [hasTparAccess, setHasTparAccess] = useState(false);
  const [error, setError] = useState({});

  const [submittedFormData, setSubmittedFormData] = useState({});
  const [showFilter, setShowFilter] = useState("");  // IRA-12: Empty string for "Show All"

  const [showModal, setShowModal] = useState(false);
  const [detailError, setDetailError] = useState({});

  const [mainGridColumns, setMainGridColumns] = useState([]);
  const [mainGridAggregates, setMainGridAggregates] = useState([]);

  const [detailsGridApiEndpoint, setDetailsGridApiEndpoint] = useState(null);
  const [detailsGridParameters, setDetailsGridParameters] = useState({});

  const [detailsGridTitle, setDetailsGridTitle] = useState("");
  const [summaryDetailsGridData, setSummaryDetailsGridData] = useState(null);

  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  const _container = React.useRef();

  const clientGridRef = useRef();
  const agingDetailsServerGridRef = React.useRef();

  useEffect(() => {
    if (
      userConfig.currentOrganization &&
      (userConfig.contactType == "PCT" || userConfig.contactType == "MCT")
    ) {
      getTPARAccess();
    } else {
      setHasTparAccess(true);
    }
  }, []);

  function getTPARAccess() {
    let accessToken = passedProps.auth.getAccessToken();

    try {
      let response = axios
        .get("api/Aging/GetTPARAccess", {
          params: {
            rpid: userConfig.currentOrganization.value,
          },
          headers: { Authorization: `Bearer ${accessToken}` },
        })
        .then((response) => {
          setHasTparAccess(response.data);
        })
        .catch(setHasTparAccess(false));
    } catch (exception) {
      setHasTparAccess(false);
    }
  }

  /*
  NOTE: The composition of the aggregates is handled by the shared grid,
  but the display of those aggregates is handled by the cell.
  */
  const sendAggregatesToParent = (aggregatesFromAgingGridSkeleton) => {
    setMainGridAggregates(aggregatesFromAgingGridSkeleton.data);
  };

  let MainGridMoneyCell = (props) => {
    let onCellClick = () => {
      let accessToken = passedProps.auth.getAccessToken();

      if (props.field === "TotalOutstanding") {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            " Total Outstanding"
        );
      } else {
        setDetailsGridTitle(
          "BIN - " +
            props.dataItem["Bin"] +
            ", " +
            props.dataItem["Payer"] +
            ", " +
            props.field +
            " Days Old"
        );
      }

      try {
        setShowModal(true);
        setDetailError({});

        let parameters1 = {
          user: user,
          pbid: JSON.stringify(props.dataItem["PBID"])
            .replace("[", "")
            .replace("]", ""),
          asOfDate: submittedFormData.toDate,
          mmids: submittedFormData.mmids,
          bin: props.dataItem["Bin"],
          period: props.field,
          payer: props.dataItem["Payer"],
          isAggregate: false,
        };

        setDetailsGridApiEndpoint("api/AgingDetails/GetByAsOfAgingServerGrid");
        setDetailsGridParameters(parameters1);

        return;
      } catch (exception) {
        onFailure(exception);
      }
    };

    let options = {
      onClick: onCellClick,
    };

    return MoneyCell(props, options);
  };

  const AggregateMainGridMoneyFooterCell = (props) => {
    let onCellClick = () => {
      if (props.field === "TotalOutstanding") {
        setDetailsGridTitle("Total Outstanding Aggregate");
      } else {
        setDetailsGridTitle(props.field + " Days Old Aggregate");
      }

      // Get all filtered data from all pages (respects current filters but includes all pages)
      const mainGridData = clientGridRef.current
        ? clientGridRef.current.GetAllFilteredData()
        : [];

      let pushPBID = [];

      for (var count = 0; count < mainGridData.length; count++) {
        pushPBID.push(mainGridData[count].PBID);
      }

      if (pushPBID.length == 0) {
        alert("No data is displayed on grid");
        return;
      }

      try {
        setShowModal(true);
        setDetailError({});

        let parameters = {
          user: user,
          pbid: JSON.stringify(pushPBID).replace("[", "").replace("]", ""),
          asOfDate: submittedFormData.toDate,
          mmids: submittedFormData.mmids,
          bin: " ",
          period: props.field,
          payer: " ",
          isAggregate: true,
        };

        setDetailsGridApiEndpoint("api/AgingDetails/GetByAsOfAgingServerGrid");
        setDetailsGridParameters(parameters);
      } catch (exception) {
        onFailure(exception);
      }
    };

    let options = {
      aggregateData: mainGridAggregates,
      onClick: onCellClick,
    };

    return AggregateMoneyFooterCell(props, options);
  };

  const AggregateMoneyDetailFooterCell = (props) => {
    if (!agingDetailsServerGridRef.current) {
      return (
        <td
          aria-colindex={props.tdProps["aria-colindex"]}
          className={props.tdProps.className}
          colSpan={props.tdProps.colSpan}
          role={props.tdProps.role}
          style={props.tdProps.style}
          key={props.tdProps.key}
        >
          {"$0.00"}
        </td>
      );
    } else {
      const gridData = agingDetailsServerGridRef.current
        ? agingDetailsServerGridRef.current.GetAllGridData()
        : null;

      if (gridData && gridData.fieldTotals) {
        let fieldTotals = gridData.fieldTotals;

        let options = {
          aggregateData: fieldTotals,
        };

        //NOTE: Okay so for some reason this is necessary
        let modifiedProps = {
          ...props,
        };
        modifiedProps.field = props.field.toLowerCase();

        return AggregateMoneyFooterCell(modifiedProps, options);
      }
    }
  };

  const asofAgingApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (IsNullOrEmpty(submittedFormData.mmids)) {
      return {};
    }

    let params = {
      user: user,
      asOfDate: submittedFormData.toDate,
      mmids: submittedFormData.mmids,
      showPayerType: submittedFormData.showPayerType || "",  // IRA-12: Include filter param
    };

    return params;
  }, [submittedFormData]);

  function handleFormSubmit(e) {
    // Clear any existing error message when initiating a new search
    setError({});
    
    setSubmittedFormData(e);
    InitiateGridDataStateReset();
  }

  function onFailure(error) {
    console.log(error);
    switch (error.response.config.url) {
      case "api/Aging/GetAsOfAging":
        setError(error.response);
        break;
      case "api/AgingDetails/GetByAsOfAging":
        setDetailError(error.response);
        setShowModal(true);
        break;
      default:
        setError(error.response);
        break;
    }
    return;
  }

  function determineMainGridColumns(data) {
    let columns = [];

    if (data.length > 0) {
      let headerProps = {
        PBID: { title: "PBID", type: "int", hidden: true },
        Payer: { title: "Payer Name", type: "text" },
        Bin: { title: "BIN", type: "text" },
        TotalOutstanding: {
          title: "Total Outstanding",
          type: "money",
          showTotal: true,
        },
      };

      Object.keys(data[0]).map((key) => {
        return columns.push({
          accessor: key,
          title: headerProps[key]
            ? headerProps[key].title
            : key.includes("+")
            ? `${key} days and over`
            : `${key} days`,
          type: headerProps[key] ? headerProps[key].type : "money",
          showTotal: headerProps[key] ? headerProps[key].showTotal : true,
          selectable: headerProps[key] ? headerProps[key].selectable : true,
          hidden: headerProps[key] ? headerProps[key].hidden : false,
        });
      });

      setMainGridColumns(columns);

      setError({});
    } else {
      //setError({ status: 201, Message: "No data found for given parameters" });
    }
  }

  let { mutate: mutateForServerSideExport } = useInsertServerSideExport();

  function detailGridErrorCallback(input) {
    if (detailsGridParameters.isAggregate) {
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostAggregateAsOfAgingDetailsExcelExportRequestToAzureStorage",
      };

      mutateForServerSideExport(mutateParameter);
    } else {
      let mutateParameter = {
        error: input,
        endpoint:
          "api/AgingDetails/PostAsOfAgingDetailsExcelExportRequestToAzureStorage",
      };

      mutateForServerSideExport(mutateParameter);
    }
  }

  function CreateGridColumn(column) {
    let columnWidth = DefaultColumnWidth();
    if (column.accessor !== "PBID") {
      let filterType = "text";
      if (column.type === "money") {
        filterType = "numeric";
      } else if (column.type === "bool") {
        filterType = "boolean";
      }

      if (column.type === "money") {
        return (
          <GridColumn
            key={column.accessor}
            filter={filterType}
            field={column.accessor}
            title={column.title}
            width={columnWidth}
            cells={{
              data: MainGridMoneyCell,
              footerCell: AggregateMainGridMoneyFooterCell,
            }}
            footerCell={AggregateMainGridMoneyFooterCell}
          />
        );
      } else {
        return (
          <GridColumn
            key={column.accessor + "Column"}
            filter={filterType}
            field={column.accessor}
            title={column.title}
            width={columnWidth}
          />
        );
      }
    }
  }

  let detailsGridUseQueryOptions = { refetchOnWindowFocus: false };

  return (
    <React.Fragment>
      {/* <LoadingSpinner
        isDataLoading={isLoading}
        controlsName={"agingAsOfAging"}
      /> */}
      {!hasTparAccess ? (
        <div>
          <br />
          <div className="bordered-control">
            <h4
              style={{
                color: "#034681",
                textAlign: "center",
              }}
            >
              No access
            </h4>
            <hr />
            <div
              style={{
                textAlign: "center",
              }}
            >
              You’ve clicked on a solution that you are currently not subscribed
              to, therefore content on this page is unavailable. If you are
              interested in enrolling in Third Party Accounts Receivable to
              enable this feature, please contact Net-Rx at sales@net-rx.com.
            </div>
          </div>
        </div>
      ) : (
        <div className="feature-tab" id="As-Of-Aging">
          <ErrorHandler
            error={error}
            onClose={() => {
              setError({});
            }}
          />
          <Accordion defaultExpanded label="Search & Filter">
            <AsOfAgingForm
              handleFormSubmit={handleFormSubmit}
              showFilter={showFilter}
              onShowFilterChange={setShowFilter}
            />
          </Accordion>

          <div ref={_container}>
            <Row>
              <Col className="tight-grid multi-line-filter">
                <SharedMainSelectableClientGrid
                  ref={clientGridRef}
                  apiGetEndpoint={"api/Aging/GetAsOfAging"}
                  apiParameters={asofAgingApiParams}
                  aggregateColumnSettings={ConstructAgingPeriodAggregateSettingsHelper(
                    agingPeriods
                  )}
                  sendAggregatesParentCallback={sendAggregatesToParent}
                  styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
                  exportFileNamePrefix={"AsOfAging"}
                  gridDataStateResetProcessingInitiated={
                    gridDataStateResetProcessingInitiated
                  }
                  onGridDataStateResetCompleted={onGridDataStateResetCompleted}
                  onDataStateChangedCallback={() => {
                    const allData = clientGridRef.current
                      ? clientGridRef.current.GetAllGridData()
                      : [];

                    if (allData) {
                      determineMainGridColumns(allData);
                    }
                  }}
                  enableExportAggregates={true}
                >
                  {mainGridColumns.length > 0
                    ? mainGridColumns.map((singleColumn) =>
                        CreateGridColumn(singleColumn)
                      )
                    : null}
                </SharedMainSelectableClientGrid>
              </Col>
            </Row>
          </div>

          <DetailsModal
            title="Payer Summary"
            show={showModal}
            handleClose={() => {
              setShowModal(false),
                setSummaryDetailsGridData(null),
                _container.current.focus();
            }}
          >
            <ErrorHandler
              error={detailError}
              onClose={() => {
                setDetailError({});
              }}
            />
            {summaryDetailsGridData ? (
              <div>
                <Row>
                  <Col className="tight-grid multi-line-filter">
                    <Grid
                      style={{
                        maxHeight: "10em",
                      }}
                      data={summaryDetailsGridData}
                    >
                      <GridColumn
                        key={"ClaimCount"}
                        field={"ClaimCount"}
                        title={"Claim Count"}
                        width={DefaultColumnWidth()}
                      />
                      <GridColumn
                        key={"Adjudicated"}
                        field={"Adjudicated"}
                        title={"Adjudicated"}
                        width={DefaultColumnWidth()}
                        cells={{ data: MoneyCell }}
                      />
                      <GridColumn
                        key={"Paid"}
                        field={"Paid"}
                        title={"Paid"}
                        width={DefaultColumnWidth()}
                        cells={{ data: MoneyCell }}
                      />
                      <GridColumn
                        key={"Outstanding"}
                        field={"Outstanding"}
                        title={"Outstanding"}
                        width={DefaultColumnWidth()}
                        cells={{ data: MoneyCell }}
                      />
                    </Grid>
                  </Col>
                </Row>
              </div>
            ) : null}
            <br />
            <h2>Aging Detail</h2>
            <h4>{detailsGridTitle}</h4>
            <ZIndexContext.Provider value={10003}>
              <Row>
                <Col className="tight-grid multi-line-filter">
                  <SharedMainSelectableServerGrid
                    ref={agingDetailsServerGridRef}
                    styleOverride={{
                      maxHeight: "30em",
                    }}
                    aggregateColumnSettings={null}
                    sendAggregatesParentCallback={() => {}}
                    sendSelectablesParentCallback={() => {}}
                    exportFileNamePrefix={"AsOfAgingAgingDetails"}
                    apiGetEndpoint={detailsGridApiEndpoint}
                    apiSubmittedFormData={detailsGridParameters}
                    gridErrorHandlerCallback={detailGridErrorCallback}
                    secondsBeforeLoadingSpinner={5}
                    useQueryOptions={detailsGridUseQueryOptions}
                    isSelectable={false}
                    onGridApiDataLoadedCallback={() => {
                      SetSummaryDetailsDataHelper(
                        agingDetailsServerGridRef,
                        setSummaryDetailsGridData
                      );
                    }}
                  >
                    <GridColumn
                      key={"ncpdp"}
                      field={"ncpdp"}
                      title={"NCPDP"}
                      width={DefaultBooleanOrShortWidthColumnWidth()}
                    />
                    <GridColumn
                      key={"rxNumber"}
                      field={"rxNumber"}
                      title={"Rx #"}
                      filter={"text"}
                      width={DefaultColumnWidth()}
                    />
                    <GridColumn
                      key={"dispensedDate"}
                      field={"dispensedDate"}
                      title={"Dispensed Date"}
                      filter={"date"}
                      format="{0:d}"
                      width={DefaultDateColumnWidth()}
                    />
                    <GridColumn
                      key={"transactionDate"}
                      field={"transactionDate"}
                      title={"Transaction Date"}
                      filter={"date"}
                      format="{0:d}"
                      width={DefaultDateColumnWidth()}
                    />
                    {detailsGridParameters.isAggregate ? (
                      <GridColumn
                        key={"payerName"}
                        field={"payerName"}
                        title={"Payer Name"}
                        width={DefaultColumnWidth()}
                      />
                    ) : null}
                    <GridColumn
                      key={"bin"}
                      field={"bin"}
                      title={"BIN"}
                      width={DefaultColumnWidth()}
                    />
                    <GridColumn
                      key={"pcn"}
                      field={"pcn"}
                      title={"PCN"}
                      width={DefaultColumnWidth()}
                    />
                    <GridColumn
                      key={"promiseToPay"}
                      field={"promiseToPay"}
                      title={"Promise to Pay"}
                      filter={"numeric"}
                      width={DefaultColumnWidth()}
                      cells={{
                        data: MoneyCell,
                        footerCell: AggregateMoneyDetailFooterCell,
                      }}
                      footerCell={AggregateMoneyDetailFooterCell}
                      headerCell={PromiseToPayHeaderCell}
                    />
                    <GridColumn
                      key={"remittancePaid"}
                      field={"remittancePaid"}
                      title={"Paid"}
                      filter={"numeric"}
                      width={DefaultColumnWidth()}
                      cells={{
                        data: MoneyCell,
                        footerCell: AggregateMoneyDetailFooterCell,
                      }}
                      footerCell={AggregateMoneyDetailFooterCell}
                    />
                    <GridColumn
                      key={"outstanding"}
                      field={"outstanding"}
                      title={"Outstanding"}
                      filter={"numeric"}
                      width={DefaultColumnWidth()}
                      cells={{
                        data: MoneyCell,
                        footerCell: AggregateMoneyDetailFooterCell,
                      }}
                      footerCell={AggregateMoneyDetailFooterCell}
                    />
                    <GridColumn
                      key={"claimNumber"}
                      field={"claimNumber"}
                      title={"Claim #"}
                      width={DefaultColumnWidth()}
                    />
                    <GridColumn
                      key={"cardholderId"}
                      field={"cardholderId"}
                      title={"Cardholder ID"}
                      width={DefaultColumnWidth()}
                    />
                    <GridColumn
                      key={"adjudicatedCoPay"}
                      field={"adjudicatedCoPay"}
                      title={"Copay"}
                      filter={"numeric"}
                      width={DefaultColumnWidth()}
                      cells={{
                        data: MoneyCell,
                        footerCell: AggregateMoneyDetailFooterCell,
                      }}
                      footerCell={AggregateMoneyDetailFooterCell}
                    />
                  </SharedMainSelectableServerGrid>
                </Col>
              </Row>
            </ZIndexContext.Provider>
          </DetailsModal>
        </div>
      )}
    </React.Fragment>
  );
}
